import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { RoutesPath } from "../constants";
import { useNavigate, useLocation } from "react-router-dom";
import { useOktaAuth } from "@okta/okta-react";
import { jwtDecode } from "jwt-decode";

const IDLE_TIMEOUT_MS = 20 * 60 * 1000; // 20 min
// const NODE_ENV = process.env.REACT_APP_ENV;
// const VERSION = process.env.REACT_APP_VERSION;
// const isDev = process.env.NODE_ENV === "development";
const APP_ENV = process.env.REACT_APP_ENV;
const isDevEnv = APP_ENV === "DEV";

const Dashboardsidebar = ({ children }) => {
  const { oktaAuth } = useOktaAuth();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [expirationTime, setExpirationTime] = useState(null);
  const dropdownRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();

  const userName = localStorage.getItem("userName") || "User";
  const firstName = userName?.split(" ")[0];

  /** =============================
   *  Logout
   ============================= */
  const handleLogout = async () => {
    try {
      await oktaAuth.signOut({
        revokeAccessToken: true,
        revokeRefreshToken: true,
        postLogoutRedirectUri: window.location.origin,
      });
    } catch (e) {
      console.error("Logout error:", e);
    }

    localStorage.clear();
    navigate(RoutesPath.LOGIN, { replace: true });
  };

  /** =============================
   *  Decode Token Expiration
   ============================= */
  const updateAccessTokenExpireTime = () => {
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        handleLogout();
        return;
      }

      const decoded = jwtDecode(token);
      setExpirationTime(decoded.exp);

      const now = Math.floor(Date.now() / 1000);
      if (decoded.exp <= now) {
        handleLogout();
      }
    } catch (e) {
      console.error("Token decode failed", e);
      handleLogout();
    }
  };

  /** =============================
   *  Check Token Expiry Timer
   ============================= */
  useEffect(() => {
    if (!expirationTime) return;

    const now = Math.floor(Date.now() / 1000);
    const msRemaining = (expirationTime - now) * 1000;

    if (msRemaining <= 0) {
      handleLogout();
      return;
    }

    const timeout = setTimeout(() => {
      handleLogout();
    }, msRemaining);

    return () => clearTimeout(timeout);
  }, [expirationTime]);

  /** =============================
   *  Okta Token Expiry Hook
   ============================= */
  useEffect(() => {
    const tm = oktaAuth.tokenManager;

    const onExpired = () => {
      handleLogout();
    };

    tm.on("expired", onExpired);
    return () => tm.off("expired", onExpired);
  }, [oktaAuth]);

  /** =============================
   *  Idle Timeout Logout
   ============================= */
  useEffect(() => {
    let idleTimer;

    const resetTimer = () => {
      clearTimeout(idleTimer);
      idleTimer = setTimeout(() => {
        handleLogout();
      }, IDLE_TIMEOUT_MS);
    };

    ["mousemove", "keypress", "click"].forEach((evt) =>
      window.addEventListener(evt, resetTimer)
    );

    resetTimer();

    return () => {
      ["mousemove", "keypress", "click"].forEach((evt) =>
        window.removeEventListener(evt, resetTimer)
      );
      clearTimeout(idleTimer);
    };
  }, []);

  /** =============================
   *  On First Load
   ============================= */
  useEffect(() => {
    updateAccessTokenExpireTime();
  }, []);

  /** =============================
   *  Close dropdown on outside click
   ============================= */
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);

    return () => document.removeEventListener("mousedown", handler);
  }, []);

  /** =============================
   *  Active Tab Highlight
   ============================= */
  const getActiveTab = () => {
    if (location.pathname === RoutesPath.DASHBOARD) return "home";
    if (location.pathname === RoutesPath.SENDPAGE) return "send";
    if (location.pathname === RoutesPath.PROFILE) return "profile";
    if (location.pathname === RoutesPath.MANAGEPAGE) return "manage";
    return "";
  };

  const active = getActiveTab();

  return (
    <div className="dashboard-layout">

      {/* Divider stands alone */}
      <div className="divider-dashboard"></div>

      {/* Sidebar must be OUTSIDE divider */}
      <div className="sidebar-dashbord">

        <div
          className={`icon-wrapper ${active === "home" ? "active" : ""}`}
          onClick={() => navigate(RoutesPath.DASHBOARD)}
        >
          <i className="fa-solid fa-house"></i>
          <span className="sidebar-tooltip">Dashboard</span>
        </div>

        <div
          className={`icon-wrapper ${active === "send" ? "active" : ""}`}
          onClick={() => navigate(RoutesPath.SENDPAGE)}
        >
          <i className="fa-solid fa-paper-plane"></i>
          <span className="sidebar-tooltip">Send</span>
        </div>

        <div
          className={`icon-wrapper ${active === "manage" ? "active" : ""}`}
          onClick={() => navigate(RoutesPath.MANAGEPAGE)}
        >
          <i className="fa-solid fa-file-signature"></i>
          <span className="sidebar-tooltip">Manage</span>
        </div>

        <div className="logout-wrapper" onClick={handleLogout}>
          <i className="fa-solid fa-right-from-bracket logout-icon"></i>
          <span className="sidebar-tooltip">Logout</span>
        </div>
      </div>

      {/* Main Content */}
      <div className="dashboard-main">
        <div className="dashboard-header">
          <div className="header-left">
            <img
              src="/assets/img/Logo.png"
              alt="Logo"
              className="header-logo"
            />
          </div>
          {/* CENTER ENV TEXT */}
          <div className="header-center">
            {isDevEnv && (
              <span className="env-text">Testing Enviornment</span>
            )}
          </div>

          <div className="header-right">
            {/* <div className="search-box">
              <i className="fa fa-search search-icon"></i>
              <input type="text" placeholder="Search for something" />
            </div> */}

            <div className="username-menu" ref={dropdownRef}>
              <span
                className="username-display"
                onClick={() => setDropdownOpen(!dropdownOpen)}
              >
                {firstName} <i className="fa fa-chevron-down"></i>
              </span>

              {dropdownOpen && (
                <div className="profile-dropdown">
                  <ul>
                    <li onClick={() => navigate(RoutesPath.PROFILE)}>
                      <i className="fa fa-user"></i> My Profile
                    </li>

                    <li onClick={handleLogout}>
                      <i className="fa fa-right-from-bracket"></i> Logout
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="dashboard-content">{children}</div>

      </div>
    </div>
  );
};

Dashboardsidebar.propTypes = {
  children: PropTypes.node,
};

export default Dashboardsidebar;